import { ELEMENT_ATTRIBUTE_GROUP, ELEMENT_GROUPS } from './../utils';

const MuscleHeatMapFields = {
  group: ELEMENT_GROUPS.OTHER,
  label: 'Muscle Heat Map',
  icon: 'DocumentTextIcon',
  description: 'A muscle heatmap for fitness apps',
  properties: [
    'layout',
    {
      label: 'Text',
      name: 'text',
      editor: 'textbox',
      description: 'The text to prepend to the field',
      default: 'Version #: ',
      group: ELEMENT_ATTRIBUTE_GROUP.CONTENT
    },
    'color',
    'font',
    'alignment',
    'margin',
    'padding',
    {
      label: 'Canvas Width',
      name: 'canvasWidth',
      editor: 'number',
      description: 'The width of the heatmap.',
      default: 400,
      group: ELEMENT_ATTRIBUTE_GROUP.LAYOUT
    },
    {
      label: 'Canvas Height',
      name: 'canvasHeight',
      editor: 'number',
      description: 'The height of the heatmap.',
      default: 400,
      group: ELEMENT_ATTRIBUTE_GROUP.LAYOUT
    },
    {
      label: 'Svg Viewbox Height',
      name: 'viewboxHeight',
      editor: 'number',
      description: 'The height of the heatmap viewbox.',
      default: 400,
      group: ELEMENT_ATTRIBUTE_GROUP.LAYOUT
    },
    {
      label: 'Svg Viewbox Width',
      name: 'viewboxWidth',
      editor: 'number',
      description: 'The width of the heatmap viewbox.',
      default: 200,
      group: ELEMENT_ATTRIBUTE_GROUP.LAYOUT
    },
    {
      label: 'Heat Image Height',
      name: 'heatImageHeight',
      editor: 'number',
      description: 'The height of a heat map image portion.',
      default: 240,
      group: ELEMENT_ATTRIBUTE_GROUP.LAYOUT
    },
    {
      label: 'Heat Image Width',
      name: 'heatImageWidth',
      editor: 'number',
      description: 'The width of the heatmap image portion.',
      default: 290,
      group: ELEMENT_ATTRIBUTE_GROUP.LAYOUT
    },
    {
      label: 'Image Start X',
      name: 'startX',
      editor: 'number',
      description: 'The x coordinate to start laying out image content from',
      default: 0,
      group: ELEMENT_ATTRIBUTE_GROUP.LAYOUT
    },
    {
      label: 'Image Start Y',
      name: 'startY',
      editor: 'number',
      description: 'The y coordinate to start laying out image content from',
      default: 0,
      group: ELEMENT_ATTRIBUTE_GROUP.LAYOUT
    },
    {
      label: 'Stat ID',
      name: 'statId',
      editor: 'textbox',
      description:
        'The ID of a stat defined on an exercise model that will be used to compute the heatmap.',
      default: '',
      group: ELEMENT_ATTRIBUTE_GROUP.CONTENT
    },
    {
      label: 'Phase 1 Duration Days',
      name: 'durationPhase1',
      editor: 'number',
      default: 1,
      description:
        'The number of days before phase 1 is over. i.e. If I worked out this morning at 10am, then phase 1 will end tomorrow at 10am, changing the images to the phase 2 images and so on',
      group: ELEMENT_ATTRIBUTE_GROUP.BEHAVIOUR
    },
    {
      label: 'Phase 2 Duration Days',
      name: 'durationPhase2',
      editor: 'number',
      default: 2,
      description:
        'The number of days before phase 1 is over. i.e. If I worked out this morning at 10am, then phase 1 will end tomorrow at 10am, changing the images to the phase 2 images and so on',
      group: ELEMENT_ATTRIBUTE_GROUP.BEHAVIOUR
    },
    {
      label: 'Phase 3 Duration Days',
      name: 'durationPhase3',
      editor: 'number',
      default: 3,
      description:
        'The number of days before phase 1 is over. i.e. If I worked out this morning at 10am, then phase 1 will end tomorrow at 10am, changing the images to the phase 2 images and so on',
      group: ELEMENT_ATTRIBUTE_GROUP.BEHAVIOUR
    },
    {
      label: 'Body Outline (Male)',
      name: 'imageBodyOutlineMale',
      editor: 'image',
      description: 'The image(s) body outline for a male.',
      mediaLibraryStoreIn: 'custom_folder',
      mediaLibraryPath: 'apps/muscles',
      allowMultipleUploads: false,
      group: ELEMENT_ATTRIBUTE_GROUP.IMAGERY
    },
    {
      label: 'Body Outline (Female)',
      name: 'imageBodyOutlineFemale',
      editor: 'image',
      description: 'The image(s) body outline for a female.',
      mediaLibraryStoreIn: 'custom_folder',
      mediaLibraryPath: 'apps/muscles',
      allowMultipleUploads: false,
      group: ELEMENT_ATTRIBUTE_GROUP.IMAGERY
    },
    {
      label: 'Glutes',
      name: 'imageGlutes',
      editor: 'image',
      description:
        'The image(s) to used on the heatmap for glutes. Images must be ordered in Phase 1 to Phase 3, with Phase 1 representing the most recent workout of that muscle group.',
      mediaLibraryStoreIn: 'custom_folder',
      mediaLibraryPath: 'apps/muscles',
      allowMultipleUploads: true,
      group: ELEMENT_ATTRIBUTE_GROUP.IMAGERY
    },
    {
      label: 'Legs (lower)',
      name: 'imageLegsLower',
      editor: 'image',
      description:
        'The image(s) to used on the heatmap for lower lges. Images must be ordered in Phase 1 to Phase 3, with Phase 1 representing the most recent workout of that muscle group.',
      mediaLibraryStoreIn: 'custom_folder',
      mediaLibraryPath: 'apps/muscles',
      allowMultipleUploads: true,
      group: ELEMENT_ATTRIBUTE_GROUP.IMAGERY
    },
    {
      label: 'Legs (upper)',
      name: 'imageLegsUpper',
      editor: 'image',
      description:
        'The image(s) to used on the heatmap for upper lges. Images must be ordered in Phase 1 to Phase 3, with Phase 1 representing the most recent workout of that muscle group.',
      mediaLibraryStoreIn: 'custom_folder',
      mediaLibraryPath: 'apps/muscles',
      allowMultipleUploads: true,
      group: ELEMENT_ATTRIBUTE_GROUP.IMAGERY
    },
    {
      label: 'Forearms',
      name: 'imageForearms',
      editor: 'image',
      description:
        'The image(s) to used on the heatmap for forearms. Images must be ordered in Phase 1 to Phase 3, with Phase 1 representing the most recent workout of that muscle group.',
      mediaLibraryStoreIn: 'custom_folder',
      mediaLibraryPath: 'apps/muscles',
      allowMultipleUploads: true,
      group: ELEMENT_ATTRIBUTE_GROUP.IMAGERY
    },
    {
      label: 'Abs',
      name: 'imageAbs',
      editor: 'image',
      description:
        'The image(s) to used on the heatmap for Abs. Images must be ordered in Phase 1 to Phase 3, with Phase 1 representing the most recent workout of that muscle group.',
      mediaLibraryStoreIn: 'custom_folder',
      mediaLibraryPath: 'apps/muscles',
      allowMultipleUploads: true,
      group: ELEMENT_ATTRIBUTE_GROUP.IMAGERY
    },
    {
      label: 'Back (Upper)',
      name: 'imageUpperBack',
      editor: 'image',
      description:
        'The image(s) to used on the heatmap for upper back. Images must be ordered in Phase 1 to Phase 3, with Phase 1 representing the most recent workout of that muscle group.',
      mediaLibraryStoreIn: 'custom_folder',
      mediaLibraryPath: 'apps/muscles',
      allowMultipleUploads: true,
      group: ELEMENT_ATTRIBUTE_GROUP.IMAGERY
    },
    {
      label: 'Back (Lower)',
      name: 'imageLowerBack',
      editor: 'image',
      description:
        'The image(s) to used on the heatmap for lower back. Images must be ordered in Phase 1 to Phase 3, with Phase 1 representing the most recent workout of that muscle group.',
      mediaLibraryStoreIn: 'custom_folder',
      mediaLibraryPath: 'apps/muscles',
      allowMultipleUploads: true,
      group: ELEMENT_ATTRIBUTE_GROUP.IMAGERY
    },
    {
      label: 'Arms and Shoulders',
      name: 'imageArmsShoulders',
      editor: 'image',
      description:
        'The image(s) to used on the heatmap for arms n shoulders. Images must be ordered in Phase 1 to Phase 3, with Phase 1 representing the most recent workout of that muscle group.',
      mediaLibraryStoreIn: 'custom_folder',
      mediaLibraryPath: 'apps/muscles',
      allowMultipleUploads: true,
      group: ELEMENT_ATTRIBUTE_GROUP.IMAGERY
    },
    {
      label: 'Chest',
      name: 'imageChest',
      editor: 'image',
      description:
        'The image(s) to used on the heatmap for chest. Images must be ordered in Phase 1 to Phase 3, with Phase 1 representing the most recent workout of that muscle group.',
      mediaLibraryStoreIn: 'custom_folder',
      mediaLibraryPath: 'apps/muscles',
      allowMultipleUploads: true,
      group: ELEMENT_ATTRIBUTE_GROUP.IMAGERY
    },
    {
      label: 'Border Width',
      name: 'borderWidth',
      editor: 'number',
      description: 'The border width of the iamge',
      default: 0,
      group: ELEMENT_ATTRIBUTE_GROUP.LAYOUT
    },
    {
      label: 'Border Radius',
      name: 'borderRadius',
      editor: 'number',
      description: 'The border radius of the image',
      default: 0,
      group: ELEMENT_ATTRIBUTE_GROUP.LAYOUT
    },
    {
      label: 'Border Color',
      name: 'borderColor',
      editor: 'color',
      description: 'The border color of the image',
      default: '',
      group: ELEMENT_ATTRIBUTE_GROUP.COLORS
    },
    {
      label: 'Background Color',
      name: 'backgroundColor',
      editor: 'color',
      description:
        'The background color of the image. Useful when an image is contained and doesnt fill the area',
      default: '',
      group: ELEMENT_ATTRIBUTE_GROUP.COLORS
    },
    {
      label: 'Border Style',
      name: 'borderStyle',
      editor: 'dropdown',
      description: 'The border style of the image',
      default: 'solid',
      options: [
        { label: 'Solid', value: 'solid' },
        { label: 'Dotted', value: 'dotted' },
        { label: 'Dashed', value: 'dashed' }
      ],
      group: ELEMENT_ATTRIBUTE_GROUP.COLORS
    }
  ]
};

export default MuscleHeatMapFields;
